"use client";

import { useCallback, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "@rmh/i18n";
import { ROUTES_PATHS } from "@rmh/defs";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
// Side-effect import: without it Amplify never picks the `?code=` off the URL
// and the OIDC redirect back from Cognito is a dead end.
import "aws-amplify/auth/enable-oauth-listener";

const providerName = process.env.NEXT_PUBLIC_WEB_OIDC_PROVIDER_NAME;

interface UseStaffSsoSignIn {
  /**
   * True while the browser is sitting on the OAuth callback URL, i.e. Amplify
   * is mid-token-exchange. The sign-in page shows a loading state instead of a
   * form the visitor never asked for.
   */
  isOidcCallback: boolean;
  /** Whether SSO is available at all. False on stacks with no OIDC provider. */
  isAvailable: boolean;
  /** Hands off to the identity provider. Navigates away on success. */
  startStaffSso: () => Promise<void>;
}

/**
 * The staff half of the sign-in page.
 *
 * Cognito is configured to send the OIDC redirect to `/sign-in`, which the
 * middleware rewrites to `/v2/sign-in` (carrying the query string), so this is
 * also where the exchange completes. On success the user is pushed at the root
 * and the middleware routes them on — the patient picker for a Provider or
 * Admin, their own dashboard for anyone else. Nothing here decides that.
 */
export const useStaffSsoSignIn = (onError: () => void): UseStaffSsoSignIn => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isOidcCallback = Boolean(searchParams?.has("code"));

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          router.push(ROUTES_PATHS.ROOT);
          break;
        case "signInWithRedirect_failure":
          console.error("Staff SSO sign-in failed", payload);
          onError();
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [router, onError]);

  const startStaffSso = useCallback(async () => {
    if (!providerName) {
      throw new Error(
        "NEXT_PUBLIC_WEB_OIDC_PROVIDER_NAME is not set, so staff SSO is unavailable"
      );
    }

    // An already-signed-in staff member does not need another round trip; the
    // middleware will route them from the root.
    const session = await fetchAuthSession();
    if (session.tokens?.accessToken) {
      router.push(ROUTES_PATHS.ROOT);
      return;
    }

    await signInWithRedirect({ provider: { custom: providerName } });
  }, [router]);

  return {
    isOidcCallback,
    isAvailable: Boolean(providerName),
    startStaffSso,
  };
};
